// 예측 보기 (design 02 12.2): each top-level category's spending for the next
// months, from the trend of the months before. Like the other views it is
// computed on the device from the rows (design 02 4.4).
//
// Rules, the same as the engine's predtrend (server/src/calc/funcs_lin.c) so
// that a cell's =predtrend(…) of the same months gives the same numbers:
// - the months are the full ones before `from` (the current month is not
//   full yet), the last `history` (12) of them, from the first month with
//   any spending in the workbook (months before it are before the book
//   began, not zeros);
// - with fewer than 3 such months there is no forecast (the view says it
//   will come once 3 months have built up);
// - otherwise each line is y = a + b·t fitted by least squares over t = 1..m
//   (TREND's line) and the forecast is t = m+1 .. m+horizon;
// - shown in whole won (rounded), and a forecast below 0 is shown as 0
//   (`clamped` says so; `raw` keeps predtrend's numbers).
import type { Id, TensorItem } from './api'
import { addMonths } from './format'
import { NONE, topOf, type ViewRow } from './views'

/** the fewest full months that make a forecast */
export const FORECAST_MIN_MONTHS = 3

export interface ForecastPoint {
  month: string
  amount: number
}

export interface ForecastLine {
  id: Id // the top-level category, 0 for none
  name: string
  history: ForecastPoint[]
  /** shown: whole won, not below 0; empty with fewer than 3 months */
  forecast: ForecastPoint[]
  /** predtrend's numbers, unrounded (may be below 0) */
  raw: number[]
  /** won per month the line goes up (+) or down (-), rounded */
  slope: number
  /** a forecast below 0 is shown as 0 */
  clamped: boolean
}

export interface Forecast {
  lines: ForecastLine[]
  total: ForecastLine
  /** the months fitted (from the first with any spending) */
  used: number
  /** whether there are enough months to forecast */
  ready: boolean
}

/**
 * predtrend of one series (server/src/calc/funcs_lin.c predict_series, the
 * same arithmetic in the same order): y = a + b·t over t = 1..m by least
 * squares, the values at t = m+1 .. m+horizon. One value stays flat; no
 * values give none.
 */
export function predtrend(ys: readonly number[], horizon: number): { values: number[]; slope: number } {
  const m = ys.length
  if (m === 0) return { values: [], slope: 0 }
  let sum = 0
  for (let t = 0; t < m; t++) sum += ys[t] ?? 0
  const ybar = sum / m
  const tbar = (m + 1) / 2
  let sxy = 0
  for (let t = 0; t < m; t++) sxy += (t + 1 - tbar) * ((ys[t] ?? 0) - ybar)
  const sxx = (m * (m * m - 1)) / 12 // the sum of (t - tbar)^2
  const slope = m > 1 ? sxy / sxx : 0
  const values: number[] = []
  for (let k = 0; k < horizon; k++) values.push(ybar + slope * (m + k + 1 - tbar))
  return { values, slope }
}

/** Shown in whole won, not below 0 */
export function shownAmount(x: number): number {
  return Math.max(0, Math.round(x))
}

export function forecast(
  rows: readonly ViewRow[],
  items: Record<number, TensorItem>,
  from: string,
  history = 12,
  horizon = 6,
): Forecast {
  const first = addMonths(from, -history)
  const months: string[] = []
  for (let i = 0; i < history; i++) months.push(addMonths(first, i))
  const index = new Map(months.map((m, i) => [m, i]))
  const byTop = new Map<Id, number[]>()
  const all = new Array<number>(history).fill(0)
  for (const r of rows) {
    if (r.kind !== 'expense') continue
    const i = index.get(r.day.slice(0, 7))
    if (i === undefined) continue
    const top = topOf(items, r.category_item)
    let line = byTop.get(top)
    if (!line) {
      line = new Array<number>(history).fill(0)
      byTop.set(top, line)
    }
    line[i] = (line[i] ?? 0) + r.amount
    all[i] = (all[i] ?? 0) + r.amount
  }
  // months before the first month with any spending are before the book began
  let skip = all.findIndex((v) => v > 0)
  if (skip < 0) skip = history
  const used = history - skip
  const ready = used >= FORECAST_MIN_MONTHS
  const make = (id: Id, name: string, values: number[]): ForecastLine => {
    const hist = months.map((month, i) => ({ month, amount: values[i] ?? 0 }))
    const fit = predtrend(
      hist.slice(skip).map((p) => p.amount),
      ready ? horizon : 0,
    )
    return {
      id,
      name,
      history: hist,
      forecast: fit.values.map((x, i) => ({ month: addMonths(from, i), amount: shownAmount(x) })),
      raw: fit.values,
      slope: ready ? Math.round(fit.slope) : 0,
      clamped: fit.values.some((x) => Math.round(x) < 0),
    }
  }
  const lines = [...byTop.entries()]
    .map(([id, values]) => make(id, id ? (items[id]?.name ?? NONE) : NONE, values))
    .sort((x, y) => sum(y.forecast) - sum(x.forecast) || x.name.localeCompare(y.name, 'ko'))
  return { lines, total: make(0, '합계', all), used, ready }
}

function sum(ps: readonly ForecastPoint[]): number {
  return ps.reduce((s, p) => s + p.amount, 0)
}
